import { tv, type VariantProps } from 'tailwind-variants';

import { CATEGORY_COLORS, type CategoryId } from '@/shared/constants/category-colors';

/**
 * The `badge-*` hue each category draws from, for hosts that need the raw CSS
 * variable rather than a class. Support takes `lime`, which Tessra labels Green.
 */
/* eslint-disable i18next/no-literal-string -- CSS variable names, not copy */
export const TOOL_BADGE_HUES = {
  plan: 'orange',
  create: 'teal',
  scale: 'red',
  deliver: 'purple',
  support: 'lime',
} as const satisfies Record<CategoryId, string>;
/* eslint-enable i18next/no-literal-string */

/**
 * Spelled out rather than built from {@link TOOL_BADGE_HUES}, since Tailwind
 * only generates classes it finds written whole. The glyph inherits the text
 * colour, so each entry sets both halves of the pair.
 */
export const TOOL_BADGE_TINTS = {
  plan: 'bg-badge-orange text-badge-orange-foreground',
  create: 'bg-badge-teal text-badge-teal-foreground',
  scale: 'bg-badge-red text-badge-red-foreground',
  deliver: 'bg-badge-purple text-badge-purple-foreground',
  support: 'bg-badge-lime text-badge-lime-foreground',
} as const satisfies Record<CategoryId, string>;

export const toolBadgeVariants = tv({
  base: 'inline-flex shrink-0 items-center justify-center',
  variants: {
    category: TOOL_BADGE_TINTS,
    shape: {
      tile: '',
      circle: 'rounded-full',
    },
    size: {
      xs: 'size-6',
      sm: 'size-8',
      md: 'size-9',
      lg: 'size-10',
      xl: 'size-12',
      '2xl': 'size-16',
    },
  },
  compoundVariants: [
    { shape: 'tile', size: ['xs', 'sm', 'md', 'lg', 'xl'], class: 'rounded-sm' },
    { shape: 'tile', size: '2xl', class: 'rounded-md' },
  ],
});

export type ToolBadgeVariantProps = VariantProps<typeof toolBadgeVariants>;
export type ToolBadgeSize = NonNullable<ToolBadgeVariantProps['size']>;

export const TOOL_BADGE_GLYPH_SIZES: Record<ToolBadgeSize, number> = {
  xs: 12,
  sm: 16,
  md: 18,
  lg: 20,
  xl: 20,
  '2xl': 38,
};

/**
 * Tags arrive in any case and are sometimes empty or unknown; those fall back
 * to `deliver`, as `ToolIconsV4` does, so tile and glyph never disagree.
 */
export function toToolBadgeCategory(tag: string | undefined): CategoryId {
  const normalized = tag?.toLowerCase() ?? '';
  return normalized in CATEGORY_COLORS ? (normalized as CategoryId) : 'deliver';
}
